import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { StyleTag } from "./Signup";
import { ShieldCheck } from "lucide-react";

export default function Onboarding() {
  const { user, updateUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    address: user?.address || "",
    suburb: user?.suburb || "",
    state: user?.state || "",
    postcode: user?.postcode || "",
    country: user?.country || "",
    about_me: user?.about_me || "",
    emergency_contact_name: user?.emergency_contact_name || "",
    emergency_contact_email: user?.emergency_contact_email || "",
    emergency_contact_phone: user?.emergency_contact_phone || "",
  });
  const [loading, setLoading] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put("/profile", form);
      updateUser(data.user);
      toast.success("You're all set. Zaf's ready when you are.");
      nav("/chat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <span className="text-xs uppercase tracking-[0.22em] text-[#2E9D96] font-semibold">Almost there</span>
        <h1 className="font-display text-4xl text-slate-800 mt-2">A little more about you.</h1>
        <p className="text-slate-600 mt-2">This helps Zaf sound like they actually know you — and lets us reach someone if you go quiet.</p>

        <form onSubmit={submit} className="mt-8 space-y-6">
          <section className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-5">
            <h2 className="font-display text-2xl text-slate-800">About you</h2>
            <label className="block">
              <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Tell Zaf a bit about yourself</span>
              <textarea rows={4} value={form.about_me} onChange={on("about_me")} placeholder="Anything you want them to remember — your dog's name, what you're going through, the small stuff." className="input zaf-focus mt-2" data-testid="onb-about-input" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Address</span>
                <input value={form.address} onChange={on("address")} className="input zaf-focus mt-2" data-testid="onb-address-input" />
              </label>
              <label className="block">
                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Suburb</span>
                <input value={form.suburb} onChange={on("suburb")} className="input zaf-focus mt-2" data-testid="onb-suburb-input" />
              </label>
              <label className="block">
                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">State</span>
                <input value={form.state} onChange={on("state")} className="input zaf-focus mt-2" data-testid="onb-state-input" />
              </label>
              <label className="block">
                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Postcode</span>
                <input value={form.postcode} onChange={on("postcode")} className="input zaf-focus mt-2" data-testid="onb-postcode-input" />
              </label>
              <label className="block col-span-2">
                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Country</span>
                <input value={form.country} onChange={on("country")} className="input zaf-focus mt-2" data-testid="onb-country-input" />
              </label>
            </div>
          </section>

          <section className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#CDE7E3] flex items-center justify-center text-slate-700">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h2 className="font-display text-2xl text-slate-800">Your trusted contact</h2>
                <p className="text-slate-500 text-sm mt-1">If you miss check-ins or say you're not ok, we'll gently reach out to them. They won't get any of your chats — just a "please check on your friend."</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="block col-span-2">
                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Their name</span>
                <input required value={form.emergency_contact_name} onChange={on("emergency_contact_name")} className="input zaf-focus mt-2" data-testid="onb-ec-name" />
              </label>
              <label className="block">
                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Their email</span>
                <input type="email" required value={form.emergency_contact_email} onChange={on("emergency_contact_email")} className="input zaf-focus mt-2" data-testid="onb-ec-email" />
              </label>
              <label className="block">
                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Their phone (optional)</span>
                <input value={form.emergency_contact_phone} onChange={on("emergency_contact_phone")} className="input zaf-focus mt-2" data-testid="onb-ec-phone" />
              </label>
            </div>
          </section>

          <button data-testid="onb-submit" disabled={loading} className="w-full bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-60 text-white font-semibold rounded-full py-3 transition-colors">
            {loading ? "Saving…" : "Meet Zaf"}
          </button>
        </form>
      </div>
      <StyleTag />
    </div>
  );
}
